// hooks/useContactForm.ts
"use client";

import { useCallback, useState } from "react";
import type { ContactReason } from "@/data/contact-page";

export type { ContactReason };

export interface ContactFormValues {
  name: string;
  email: string;
  reason: ContactReason;
  message: string;
}

export interface ContactFormErrors {
  name?: string;
  email?: string;
  message?: string;
}

export type SubmitStatus = "idle" | "submitting" | "success" | "error";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_VALUES: ContactFormValues = {
  name: "",
  email: "",
  reason: "general",
  message: "",
};

function validate(values: ContactFormValues): ContactFormErrors {
  const errors: ContactFormErrors = {};

  if (!values.name.trim()) {
    errors.name = "Please enter your name";
  } else if (values.name.trim().length < 2) {
    errors.name = "Name must be at least 2 characters";
  }

  if (!values.email.trim()) {
    errors.email = "Please enter your email";
  } else if (!EMAIL_RE.test(values.email.trim())) {
    errors.email = "Please enter a valid email address";
  }

  if (!values.message.trim()) {
    errors.message = "Please add a message";
  } else if (values.message.trim().length < 10) {
    errors.message = "Message must be at least 10 characters";
  }

  return errors;
}

interface UseContactFormOptions {
  onSubmit?: (values: ContactFormValues) => Promise<void>;
}

export function useContactForm(options: UseContactFormOptions = {}) {
  const { onSubmit } = options;
  const [values, setValues] = useState<ContactFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof ContactFormValues, boolean>>>({});
  const [status, setStatus] = useState<SubmitStatus>("idle");

  const setField = useCallback(
    <K extends keyof ContactFormValues>(field: K, value: ContactFormValues[K]) => {
      setValues((prev) => {
        const next = { ...prev, [field]: value };
        if (touched[field]) {
          const nextErrors = validate(next);
          setErrors((e) => ({
            ...e,
            [field]: nextErrors[field as keyof ContactFormErrors],
          }));
        }
        return next;
      });
    },
    [touched]
  );

  const setTouchedField = useCallback(
    (field: keyof ContactFormValues) => {
      setTouched((t) => ({ ...t, [field]: true }));
      const currentErrors = validate(values);
      setErrors((e) => ({
        ...e,
        [field]: currentErrors[field as keyof ContactFormErrors],
      }));
    },
    [values]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const allTouched = { name: true, email: true, reason: true, message: true };
      setTouched(allTouched);
      const nextErrors = validate(values);
      setErrors(nextErrors);

      if (Object.keys(nextErrors).length > 0) return;

      setStatus("submitting");
      try {
        if (onSubmit) {
          await onSubmit(values);
        } else {
          await new Promise<void>((resolve) => setTimeout(resolve, 1200));
        }
        setStatus("success");
        setValues(EMPTY_VALUES);
        setTouched({});
        setErrors({});
      } catch {
        setStatus("error");
      }
    },
    [values, onSubmit]
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setValues(EMPTY_VALUES);
    setErrors({});
    setTouched({});
  }, []);

  return {
    values,
    errors,
    status,
    touched,
    setField,
    setTouchedField,
    handleSubmit,
    reset,
  };
}